import { useEffect, useState } from 'react'
import { Button, Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '~/shared/ui'
import { EMPTY_ADJUSTMENT, isAdjustmentValid } from '../../../lib'
import type { AdjustmentFormValues } from '../../../model'
import { AdjustmentFormFields } from './adjustment-form-fields'
import { CollapsedAdjustmentCard } from './collapsed-adjustment-card'

interface AdjustmentSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialAdjustments: AdjustmentFormValues[]
  onSave: (adjustments: AdjustmentFormValues[]) => void
}

export function AdjustmentSheet({
  open,
  onOpenChange,
  initialAdjustments,
  onSave,
}: AdjustmentSheetProps) {
  const [adjustments, setAdjustments] = useState<AdjustmentFormValues[]>([])
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  useEffect(() => {
    if (open) {
      if (initialAdjustments.length === 0) {
        setAdjustments([{ ...EMPTY_ADJUSTMENT }])
        setEditingIndex(0)
      } else {
        setAdjustments(initialAdjustments.map((a) => ({ ...a })))
        setEditingIndex(null)
      }
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  function updateAdjustment(index: number, patch: Partial<AdjustmentFormValues>) {
    setAdjustments((prev) => prev.map((a, i) => (i === index ? { ...a, ...patch } : a)))
  }

  function handleAddAdjustment() {
    setAdjustments((prev) => [...prev, { ...EMPTY_ADJUSTMENT }])
    setEditingIndex(adjustments.length)
  }

  function handleDelete(index: number) {
    setAdjustments((prev) => prev.filter((_, i) => i !== index))
    if (editingIndex === index) {
      setEditingIndex(null)
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1)
    }
  }

  const hasInvalidAdjustment = adjustments.some((a) => !isAdjustmentValid(a))
  const isSaveDisabled = adjustments.length > 0 && hasInvalidAdjustment

  function handleSave() {
    const validAdjustments = adjustments.filter(isAdjustmentValid)
    onSave(validAdjustments)
    onOpenChange(false)
  }

  function handleCancel() {
    onOpenChange(false)
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Payment Adjustment</SheetTitle>
        </SheetHeader>

        <div className="flex flex-1 flex-col overflow-y-auto p-4">
          <div className="flex flex-col gap-4">
            {adjustments.map((adj, index) =>
              editingIndex === index ? (
                <AdjustmentFormFields
                  key={index}
                  values={adj}
                  onChange={(patch) => updateAdjustment(index, patch)}
                  onDelete={() => handleDelete(index)}
                />
              ) : (
                <CollapsedAdjustmentCard
                  key={index}
                  adjustment={adj}
                  onExpand={() => setEditingIndex(index)}
                  onDelete={() => handleDelete(index)}
                />
              ),
            )}
          </div>

          <Button
            className="mt-2"
            size="small"
            materialIcon="add"
            disabled={editingIndex !== null && !isAdjustmentValid(adjustments[editingIndex])}
            onClick={handleAddAdjustment}
          >
            Adjustment Type
          </Button>
        </div>

        <SheetFooter className="flex items-center justify-end gap-4">
          <Button
            size="small"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            raised
            size="small"
            disabled={isSaveDisabled}
            onClick={handleSave}
          >
            Save Adjustment
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
